import React, { useCallback, useEffect, useState } from 'react';
import ProfileSectionAddButton from './ProfileSectionAddButton';

const KINDS = [
  { value: 'home', label: 'Home' },
  { value: 'mailing', label: 'Mailing' },
  { value: 'other', label: 'Other' }
];

const KIND_LABELS = Object.fromEntries(KINDS.map((k) => [k.value, k.label]));

const EMPTY_FORM = {
  kind: 'home',
  line1: '',
  line2: '',
  city: '',
  region: '',
  postal_code: '',
  country: '',
  is_primary: true
};

function formatAddressLines(row) {
  const cityLine = [row.city, row.region].filter(Boolean).join(', ');
  const cityZip = [cityLine, row.postal_code].filter(Boolean).join(' ');
  return [row.line1, row.line2, cityZip, row.country].filter(Boolean);
}

/**
 * Addresses on the user profile. Always shown — not an assigned app.
 * Rows live in addresses, owned by users.id.
 */
const AddressesSection = ({ userId, canEdit, fetchWithAuth, isExpanded, onToggle }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await fetchWithAuth(`/api/addresses?user_id=${encodeURIComponent(userId)}`);
      const data = await res.json();
      if (data.success) setRows(data.data || []);
      else setError(data.message || 'Failed to load addresses');
    } catch {
      setError('Failed to load addresses');
    } finally {
      setLoading(false);
    }
  }, [userId, fetchWithAuth]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setError('');
    setConfirmRemove(false);
    setForm({ ...EMPTY_FORM, is_primary: rows.length === 0 });
    setSheet({ mode: 'add' });
  };

  const openEdit = (row) => {
    if (!canEdit) return;
    setError('');
    setConfirmRemove(false);
    setForm({
      kind: row.kind || 'home',
      line1: row.line1 || '',
      line2: row.line2 || '',
      city: row.city || '',
      region: row.region || '',
      postal_code: row.postal_code || '',
      country: row.country || '',
      is_primary: !!row.is_primary
    });
    setSheet({ mode: 'edit', id: row.id });
  };

  const closeSheet = () => {
    if (saving) return;
    setSheet(null);
    setConfirmRemove(false);
    setError('');
  };

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const body = {
        user_id: userId,
        kind: form.kind,
        line1: form.line1,
        line2: form.line2,
        city: form.city,
        region: form.region,
        postal_code: form.postal_code,
        country: form.country,
        is_primary: form.is_primary
      };
      const isEdit = sheet?.mode === 'edit';
      const res = await fetchWithAuth(isEdit ? `/api/addresses/${sheet.id}` : '/api/addresses', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Failed to save address');
        return;
      }
      setSheet(null);
      await load();
    } catch {
      setError('Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (sheet?.mode !== 'edit') return;
    setSaving(true);
    setError('');
    try {
      const res = await fetchWithAuth(`/api/addresses/${sheet.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Failed to remove address');
        return;
      }
      setSheet(null);
      setConfirmRemove(false);
      await load();
    } catch {
      setError('Failed to remove address');
    } finally {
      setSaving(false);
    }
  };

  const otherPrimaryExists = rows.some((row) => row.is_primary && row.id !== sheet?.id);
  const primaryLocked =
    (sheet?.mode === 'add' && rows.length === 0) ||
    (sheet?.mode === 'edit' && form.is_primary && !otherPrimaryExists);

  return (
    <div
      className="profile-expandable-section"
      style={{ marginBottom: 10, overflow: 'hidden', background: '#fff' }}
    >
      <div
        className="profile-expandable-section-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          background: '#fff',
          borderBottom: '1px solid #f1f5f9',
          cursor: 'pointer',
          userSelect: 'none'
        }}
        onClick={onToggle}
      >
        <span className="profile-section-title">Addresses</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {canEdit && isExpanded ? (
            <ProfileSectionAddButton title="Add address" onClick={openAdd} />
          ) : null}
          {isExpanded ? <span className="profile-section-expand">▼</span> : null}
        </div>
      </div>
      {isExpanded ? (
        <div style={{ padding: 12, background: '#fff' }}>
          {loading && rows.length === 0 ? (
            <p style={{ color: '#6b7280', fontStyle: 'italic', textAlign: 'center', padding: 12 }}>
              Loading…
            </p>
          ) : rows.length === 0 ? (
            <p style={{ color: '#6b7280', fontStyle: 'italic', textAlign: 'center', padding: 20 }}>
              No address yet.
            </p>
          ) : (
            rows.map((row) => {
              const lines = formatAddressLines(row);
              return (
                <div
                  key={row.id}
                  role={canEdit ? 'button' : undefined}
                  tabIndex={canEdit ? 0 : undefined}
                  className={`profile-pl-row${canEdit ? '' : ' profile-pl-row--disabled'}`}
                  onClick={() => openEdit(row)}
                  onKeyDown={(event) => {
                    if (canEdit && event.key === 'Enter') openEdit(row);
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span
                        style={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 6,
                          background: '#e0f2fe',
                          color: '#0369a1',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em'
                        }}
                      >
                        {KIND_LABELS[row.kind] || row.kind}
                      </span>
                      {row.is_primary ? (
                        <span
                          style={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            padding: '2px 6px',
                            borderRadius: 4,
                            background: '#d1fae5',
                            color: '#059669'
                          }}
                        >
                          Primary
                        </span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#0f172a', lineHeight: 1.4 }}>
                      {lines.length ? lines.map((line, index) => <div key={`${index}-${line}`}>{line}</div>) : 'Empty address'}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : null}

      {sheet ? (
        <div className="profile-sheet-overlay profile-sheet-overlay--raised" onClick={closeSheet}>
          <div
            className="profile-sheet-panel profile-sheet-panel--form"
            role="dialog"
            aria-modal="true"
            aria-labelledby="address-sheet-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="profile-sheet-header">
              <div className="profile-sheet-header-main">
                <h2 id="address-sheet-title" className="profile-sheet-title">
                  {sheet.mode === 'edit' ? 'Edit address' : 'Add address'}
                </h2>
                <p className="profile-sheet-subtext">
                  Saved on this person. It stays with them across enrollments.
                </p>
              </div>
              <button type="button" className="profile-sheet-close" onClick={closeSheet} aria-label="Close">
                ×
              </button>
            </div>
            <div className="profile-sheet-form-body">
              <div className="profile-sheet-form-group">
                <label className="profile-sheet-label" htmlFor="address-kind">Kind</label>
                <select
                  id="address-kind"
                  name="kind"
                  className="profile-sheet-select"
                  value={form.kind}
                  onChange={onChange}
                >
                  {KINDS.map((kind) => (
                    <option key={kind.value} value={kind.value}>{kind.label}</option>
                  ))}
                </select>
              </div>
              <div className="profile-sheet-form-group">
                <label className="profile-sheet-label" htmlFor="address-line1">Street</label>
                <input
                  id="address-line1"
                  name="line1"
                  className="profile-sheet-input"
                  value={form.line1}
                  onChange={onChange}
                  placeholder="Street address"
                />
              </div>
              <div className="profile-sheet-form-group">
                <label className="profile-sheet-label" htmlFor="address-line2">Street line 2</label>
                <input
                  id="address-line2"
                  name="line2"
                  className="profile-sheet-input"
                  value={form.line2}
                  onChange={onChange}
                  placeholder="Apartment, suite, unit"
                />
              </div>
              <div className="profile-sheet-form-group">
                <label className="profile-sheet-label" htmlFor="address-city">City</label>
                <input
                  id="address-city"
                  name="city"
                  className="profile-sheet-input"
                  value={form.city}
                  onChange={onChange}
                />
              </div>
              <div className="profile-sheet-form-group">
                <label className="profile-sheet-label" htmlFor="address-region">State</label>
                <input
                  id="address-region"
                  name="region"
                  className="profile-sheet-input"
                  value={form.region}
                  onChange={onChange}
                />
              </div>
              <div className="profile-sheet-form-group">
                <label className="profile-sheet-label" htmlFor="address-postal">ZIP</label>
                <input
                  id="address-postal"
                  name="postal_code"
                  className="profile-sheet-input"
                  value={form.postal_code}
                  onChange={onChange}
                />
              </div>
              <div className="profile-sheet-form-group">
                <label className="profile-sheet-label" htmlFor="address-country">Country</label>
                <input
                  id="address-country"
                  name="country"
                  className="profile-sheet-input"
                  value={form.country}
                  onChange={onChange}
                />
              </div>
              <label className={`profile-sheet-check${primaryLocked ? ' is-disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={primaryLocked ? true : form.is_primary}
                  disabled={primaryLocked}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, is_primary: event.target.checked }))
                  }
                />
                Primary address
                {primaryLocked && rows.length === 0 ? ' (only address)' : ''}
                {primaryLocked && rows.length > 0 ? ' (mark another address primary to change this)' : ''}
              </label>
              {error ? (
                <p className="profile-sheet-callout-text" style={{ color: '#b91c1c', marginTop: 12 }}>
                  {error}
                </p>
              ) : null}
            </div>
            <div className="profile-sheet-actions">
              {sheet.mode === 'edit' ? (
                <button
                  type="button"
                  className="profile-sheet-btn profile-sheet-btn--secondary"
                  disabled={saving}
                  onClick={() => {
                    if (confirmRemove) remove();
                    else setConfirmRemove(true);
                  }}
                >
                  {confirmRemove ? 'Confirm remove' : 'Remove'}
                </button>
              ) : (
                <button
                  type="button"
                  className="profile-sheet-btn profile-sheet-btn--secondary"
                  onClick={closeSheet}
                  disabled={saving}
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                className="profile-sheet-btn profile-sheet-btn--primary"
                disabled={saving}
                onClick={save}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AddressesSection;
